if (!customElements.get('wlsn-video')) {
  class WLSNVideo extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        playButton: 'js-wlsn-video-play-button',
        playing: 'playing',
      };

      this.selectors = this.getSelectors();
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
        threshold: 0.5,
      });
    }

    /**
     * @returns {Object}
     * @description Get selectors
     */
    getSelectors() {
      return {
        playButton: this.querySelector(`.${this.classes.playButton}`),
        video: this.querySelector('video'),
      };
    }

    /**
     * @description Add event listeners and start observing the video
     */
    connectedCallback() {
      if (this.selectors.video) {
        this.observer.observe(this);

        // Handle autoplay videos
        if (this.selectors.video.hasAttribute('autoplay')) {
          this.handleAutoplayVideo();
        } else if (this.selectors.playButton) {
          this.selectors.playButton.addEventListener('click', this.videoPlayingHandler.bind(this));
        }
      }
    }

    /**
     * @description Handle autoplay video setup
     */
    handleAutoplayVideo() {
      // Remove click listener if it exists
      if (this.selectors.playButton) {
        this.selectors.playButton.style.pointerEvents = 'none';
      }

      // Add playing class when video starts playing
      this.selectors.video.addEventListener('playing', () => {
        this.classList.add(this.classes.playing);
      });
    }

    /**
     * @description Disconnect the observer and remove the event listener
     */
    disconnectedCallback() {
      if (this.selectors.playButton && !this.selectors.video?.hasAttribute('autoplay')) {
        this.selectors.playButton.removeEventListener('click', this.videoPlayingHandler);
      }
      this.observer.disconnect();
    }

    /**
     * @param {IntersectionObserverEntry[]} entries
     */
    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting && !this.selectors.video.paused) {
          this.videoPause();
        }
      });
    }

    /**
     * @description Pause all other synchronized videos
     */
    pauseSyncedVideos() {
      if (this.hasAttribute('data-sync-pause')) {
        const allSyncedVideos = document.querySelectorAll('wlsn-video[data-sync-pause]');
        allSyncedVideos.forEach((videoElement) => {
          if (videoElement !== this && !videoElement.querySelector('video')?.paused) {
            videoElement.classList.remove(this.classes.playing);
            videoElement.querySelector('video')?.pause();
          }
        });
      }
    }

    /**
     * @description Handle video playing and pausing
     */
    videoPlayingHandler() {
      if (this.selectors.video.paused) {
        this.pauseSyncedVideos();
        this.videoPlay();
      } else {
        this.videoPause();
      }
    }

    /**
     * @description Play the video
     */
    videoPlay() {
      this.selectors.video.play();
      this.classList.add(this.classes.playing);
    }

    /**
     * @description Pause the video
     */
    videoPause() {
      // Only pause if it's not an autoplay video
      if (!this.selectors.video.hasAttribute('autoplay')) {
        this.selectors.video.pause();
        this.classList.remove(this.classes.playing);
      }
    }
  }

  customElements.define('wlsn-video', WLSNVideo);
}
